// BookingConfirmPage: Confirmation step - with payment (Stripe/PayPal) or without (reservation only)
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBooking } from "../context/BookingContext";
import { createCheckoutSession } from "../api/stripe";
import { createPayPalOrder, capturePayPalPayment } from "../api/paypal";
import { getDepositPercentage } from "../api/settings";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import AlertMessage from "../components/AlertMessage";
import { useSettings } from "../context/SettingsContext";
import bookingApi from "../api/booking";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

export default function BookingConfirmPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedTour, travelers, resetBooking } = useBooking();
  const { settings } = useSettings();

  // Get booking data from navigation state
  const bookingData = location.state?.bookingData;

  const waPhone = (settings.company_whatsapp || "+212771373922").replace(/[^0-9]/g, "");
  const currencySymbol = settings.currency_symbol || "$";
  const paymentEnabled = settings.payment_enabled === true || settings.payment_enabled === 'true' || settings.payment_enabled === '1';

  const [depositPercentage, setDepositPercentage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [error, setError] = useState("");

  // Calculate prices with discount
  const discount = Number(settings.discount_percentage) || 0;
  const originalPricePerPerson = selectedTour?.price || 0;
  const discountedPricePerPerson = discount > 0 ? originalPricePerPerson * (1 - discount / 100) : originalPricePerPerson;
  const totalPrice = discountedPricePerPerson * travelers;
  const originalTotalPrice = originalPricePerPerson * travelers;
  const depositAmount = Math.round(totalPrice * (depositPercentage / 100) * 100) / 100;
  const remainingAmount = totalPrice - depositAmount;

  // Fetch deposit percentage on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getDepositPercentage();
        if (response.deposit_percentage) {
          setDepositPercentage(response.deposit_percentage);
        }
      } catch (err) {
        console.error("Failed to fetch deposit percentage:", err);
      }
    };
    fetchSettings();
  }, []);

  // Redirect if no booking data
  useEffect(() => {
    if (!bookingData || !selectedTour) {
      navigate("/booking");
    }
  }, [bookingData, selectedTour, navigate]);

  // Handle reservation without payment
  const handleReserveWithoutPayment = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await bookingApi.createBooking({
        tour_id: bookingData.tourId,
        customer_name: bookingData.customerName,
        email: bookingData.email,
        phone: bookingData.phone,
        booking_date: bookingData.date,
        number_of_people: bookingData.travelers,
        payment_method: "none",
      });

      const booking = response.data?.booking || response.data;

      if (bookingData?.tourId) {
        sessionStorage.removeItem(`booking_form_${bookingData.tourId}`);
      }
      resetBooking();
      navigate("/booking/reserved", {
        state: {
          booking,
          tour: selectedTour,
          customerName: bookingData.customerName,
          email: bookingData.email,
          phone: bookingData.phone,
        },
      });
    } catch (e) {
      console.error("Reservation error:", e);
      setError(e.response?.data?.message || e.message || "Error creating reservation");
    } finally {
      setLoading(false);
    }
  };

  // Handle Stripe payment
  const handlePayWithCard = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await createCheckoutSession({
        tour_id: bookingData.tourId,
        customer_name: bookingData.customerName,
        customer_email: bookingData.email,
        customer_phone: bookingData.phone,
        booking_date: bookingData.date,
        number_of_people: bookingData.travelers,
      });

      if (response.success && response.checkout_url) {
        if (bookingData?.tourId) {
          sessionStorage.removeItem(`booking_form_${bookingData.tourId}`);
        }
        resetBooking();
        window.location.href = response.checkout_url;
      } else {
        throw new Error(response.error || "Failed to create payment session");
      }
    } catch (e) {
      console.error("Payment error:", e);
      setError(e.response?.data?.error || e.message || "Error initializing payment");
    } finally {
      setLoading(false);
    }
  };

  // PayPal: Create order on the backend
  const handlePayPalCreateOrder = useCallback(async () => {
    try {
      setError("");
      const response = await createPayPalOrder({
        tour_id: bookingData.tourId,
        customer_name: bookingData.customerName,
        customer_email: bookingData.email,
        customer_phone: bookingData.phone,
        booking_date: bookingData.date,
        number_of_people: bookingData.travelers,
      });

      if (response.success && response.order_id) {
        // Store booking_id for capture step
        window._paypalBookingId = response.booking_id;
        return response.order_id;
      } else {
        throw new Error(response.error || "Failed to create PayPal order");
      }
    } catch (e) {
      console.error("PayPal create order error:", e);
      setError(e.response?.data?.error || e.message || "Error creating PayPal order");
      throw e;
    }
  }, [bookingData]);

  // PayPal: Capture payment after user approval
  const handlePayPalApprove = useCallback(async (data) => {
    try {
      setPaypalLoading(true);
      setError("");

      const response = await capturePayPalPayment(
        data.orderID,
        window._paypalBookingId
      );

      if (response.success) {
        if (bookingData?.tourId) {
          sessionStorage.removeItem(`booking_form_${bookingData.tourId}`);
        }
        resetBooking();
        navigate("/booking/success", {
          state: {
            booking: response.booking,
            paymentMethod: "paypal",
          }
        });
      } else {
        throw new Error(response.error || "Payment capture failed");
      }
    } catch (e) {
      console.error("PayPal capture error:", e);
      setError(e.response?.data?.error || e.message || "Error capturing PayPal payment");
    } finally {
      setPaypalLoading(false);
    }
  }, [resetBooking, navigate, bookingData]);

  // Calculate end date
  const calculateEndDate = () => {
    if (!selectedTour?.start_date || !selectedTour?.durationDays) return null;
    const startDate = new Date(selectedTour.start_date);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + selectedTour.durationDays - 1);
    return endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (!bookingData || !selectedTour) {
    return null;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Confirm Your Booking
        </h1>
        <p className="mt-2 text-slate-600 dark:text-slate-400">
          {paymentEnabled
            ? "Please review your information and proceed to payment"
            : "Please review your information and confirm your reservation"}
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <AlertMessage type="error" onClose={() => setError("")}>
            {error}
          </AlertMessage>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column - Booking Details */}
        <div className="space-y-6">
          {/* Customer Information */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Your Information
              </h2>
              <button
                onClick={() => navigate("/booking", { state: { bookingData } })}
                className="text-sm text-emerald-600 hover:text-emerald-700"
              >
                Edit
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Name</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {bookingData.customerName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Email</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {bookingData.email}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Phone</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {bookingData.phone}
                </span>
              </div>
            </div>
          </div>

          {/* Tour Information */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
            <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
              Tour Details
            </h2>
            <div className="flex gap-4">
              <img
                src={selectedTour.thumbnail || "/placeholder-tour.jpg"}
                alt={selectedTour.title}
                className="h-24 w-24 rounded-lg object-cover"
              />
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                  {selectedTour.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {selectedTour.city} - {selectedTour.durationDays} days
                </p>
                <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  {new Date(selectedTour.start_date).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                  {calculateEndDate() && <> &rarr; {calculateEndDate()}</>}
                </div>
              </div>
            </div>
            <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Travelers</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {travelers} {travelers > 1 ? "people" : "person"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Payment or Reservation */}
        <div className="space-y-6">
          {/* Price Breakdown */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
            <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {paymentEnabled ? "Payment Summary" : "Booking Summary"}
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">
                  {discount > 0 ? (
                    <>
                      <span className="line-through text-slate-400">{currencySymbol}{originalPricePerPerson}</span>
                      {" "}
                      <span className="text-emerald-600 font-semibold">{currencySymbol}{discountedPricePerPerson.toFixed(2)}</span>
                      {" "}&times; {travelers} {travelers > 1 ? "travelers" : "traveler"}
                    </>
                  ) : (
                    <>{currencySymbol}{selectedTour.price} &times; {travelers} {travelers > 1 ? "travelers" : "traveler"}</>
                  )}
                </span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {currencySymbol}{totalPrice.toFixed(2)}
                </span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-red-500 dark:text-red-400 flex items-center gap-1">
                    <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded-full font-semibold">-{discount}%</span>
                    Discount applied
                  </span>
                  <span className="font-medium text-red-500 dark:text-red-400">
                    -{currencySymbol}{(originalTotalPrice - totalPrice).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="border-t border-slate-200 pt-3 dark:border-slate-700">
                <div className="flex justify-between">
                  <span className="text-slate-900 dark:text-slate-100">Total Price</span>
                  <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    {currencySymbol}{totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Deposit Info - only when payment is enabled */}
            {paymentEnabled && (
              <div className="mt-6 rounded-lg bg-emerald-50 p-4 dark:bg-emerald-900/20">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-emerald-900 dark:text-emerald-100">
                      Pay {depositPercentage}% Deposit Now
                    </p>
                    <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                      Secure your spot with a deposit. Pay the remaining balance on the day of the tour.
                    </p>
                  </div>
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-emerald-700 dark:text-emerald-300">Deposit ({depositPercentage}%)</span>
                    <span className="font-bold text-emerald-900 dark:text-emerald-100">
                      {currencySymbol}{depositAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-emerald-700 dark:text-emerald-300">Remaining (pay on tour day)</span>
                    <span className="font-medium text-emerald-900 dark:text-emerald-100">
                      {currencySymbol}{remainingAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* No-payment info */}
            {!paymentEnabled && (
              <div className="mt-6 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      No payment required now
                    </p>
                    <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                      After confirming, our team will contact you within 24 hours to finalize the details and arrange payment.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Payment Options (when enabled) or Confirm Button (when disabled) */}
          {paymentEnabled ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
              <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
                Choose Payment Method
              </h2>

              {/* Pay with Card (Stripe) */}
              <button
                onClick={handlePayWithCard}
                disabled={loading || paypalLoading}
                className="mb-3 flex w-full items-center justify-center gap-3 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 font-semibold text-white transition-all hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Redirecting to Stripe...
                  </>
                ) : (
                  <>
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 10h18v7a3 3 0 01-3 3H6a3 3 0 01-3-3v-7zm0-3a3 3 0 013-3h12a3 3 0 013 3v1H3V7z" />
                    </svg>
                    Pay {currencySymbol}{depositAmount.toFixed(2)} with Card
                  </>
                )}
              </button>

              <div className="mb-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                <img src="https://cdn.jsdelivr.net/gh/nicepkg/vr360@master/public/visa.svg" alt="Visa" className="h-6" onError={(e) => e.target.style.display='none'} />
                <img src="https://cdn.jsdelivr.net/gh/nicepkg/vr360@master/public/mastercard.svg" alt="Mastercard" className="h-6" onError={(e) => e.target.style.display='none'} />
                <span>Visa, Mastercard, Amex accepted</span>
              </div>

              {/* Divider */}
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-slate-500 dark:bg-slate-900 dark:text-slate-400">or</span>
                </div>
              </div>

              {/* PayPal */}
              {PAYPAL_CLIENT_ID ? (
                <div className="mb-4">
                  <PayPalScriptProvider options={{
                    "client-id": PAYPAL_CLIENT_ID,
                    currency: "USD",
                    intent: "capture",
                  }}>
                    <PayPalButtons
                      style={{
                        layout: "horizontal",
                        color: "gold",
                        shape: "rect",
                        label: "paypal",
                        height: 50,
                      }}
                      disabled={loading || paypalLoading}
                      createOrder={handlePayPalCreateOrder}
                      onApprove={handlePayPalApprove}
                      onError={(err) => {
                        console.error("PayPal error:", err);
                        setError("PayPal payment failed. Please try again or use card payment.");
                      }}
                      onCancel={() => {
                        setError("");
                      }}
                    />
                  </PayPalScriptProvider>
                  {paypalLoading && (
                    <div className="mt-2 flex items-center justify-center gap-2 text-sm text-slate-600">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                      Processing PayPal payment...
                    </div>
                  )}
                </div>
              ) : (
                <button
                  disabled
                  className="mb-4 flex w-full cursor-not-allowed items-center justify-center gap-3 rounded-lg border-2 border-[#ffc439] bg-[#ffc439]/10 px-6 py-4 font-semibold text-slate-400"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 01-.633-.74L4.944 3.72a.77.77 0 01.757-.629h6.622c2.184 0 3.94.616 5.024 1.76.49.518.838 1.12 1.032 1.779.202.69.258 1.508.166 2.432-.046.468-.13.936-.249 1.404-.41 1.577-1.177 2.89-2.245 3.833-1.107.97-2.524 1.545-4.21 1.71l-.005.001h-3.31a.77.77 0 00-.756.628l-.848 5.699zm13.166-14.79c-.047.308-.099.611-.156.91-.726 3.81-3.211 5.13-6.388 5.13H11.27l-.893 5.66h2.308a.539.539 0 00.532-.44l.022-.114.422-2.67.027-.148a.539.539 0 01.532-.44h.335c2.168 0 3.864-.88 4.362-3.424.207-1.065.1-1.954-.448-2.577-.166-.19-.373-.346-.605-.473l-.022-.014z" />
                  </svg>
                  PayPal - Configuration Required
                </button>
              )}

              {/* Other Payment Methods */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      Need another payment method?
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      Contact us via WhatsApp and we'll help you complete your booking.
                    </p>
                    <a
                      href={`https://wa.me/${waPhone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-green-600 hover:text-green-700"
                    >
                      <span>Chat with us on WhatsApp</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* No-payment mode: Confirm Reservation button */
            <div className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
              <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
                Confirm Your Reservation
              </h2>

              <div className="mb-4 space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600 dark:bg-blue-900/40">1</span>
                  <span>We'll receive your reservation details immediately</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600 dark:bg-blue-900/40">2</span>
                  <span>Our team will contact you within 24 hours to confirm</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600 dark:bg-blue-900/40">3</span>
                  <span>Payment will be arranged directly with our team</span>
                </div>
              </div>

              <button
                onClick={handleReserveWithoutPayment}
                disabled={loading}
                className="flex w-full items-center justify-center gap-3 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 font-semibold text-white transition-all hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Confirm Reservation
                  </>
                )}
              </button>

              {/* WhatsApp contact */}
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      Questions? Contact us
                    </p>
                    <a
                      href={`https://wa.me/${waPhone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-2 text-sm font-medium text-green-600 hover:text-green-700"
                    >
                      Chat with us on WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security / Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
            {paymentEnabled ? (
              <>
                <div className="flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-emerald-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
                  </svg>
                  <span>256-bit SSL encryption</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Secure payments by Stripe & PayPal</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Instant confirmation email</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="h-4 w-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Free cancellation 48h before</span>
                </div>
              </>
            )}
          </div>

          {/* Back Button */}
          <button
            onClick={() => navigate("/booking", { state: { bookingData } })}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-6 py-3 font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Edit
          </button>
        </div>
      </div>
    </div>
  );
}
